import { createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import { Settings, WifiOff } from "lucide-react";
import { ErrorBoundary } from "./components/shared/ErrorBoundary";
import { BottomNav } from "./components/shared/BottomNav";
import { useSettings } from "./hooks/useSettings";
import { useAutoReload } from "./hooks/useAutoReload";
import { useNetworkStatus } from "./hooks/useNetworkStatus";
import type { AppSettings, Child, Location, AppPrefs } from "./types/index";

// Lazy-load screens to keep initial bundle small
import { lazy, Suspense } from "react";
const DashboardScreen = lazy(() =>
  import("./components/Dashboard/DashboardScreen").then((m) => ({
    default: m.DashboardScreen,
  }))
);
const CalendarScreen = lazy(() =>
  import("./components/Calendar/CalendarScreen").then((m) => ({
    default: m.CalendarScreen,
  }))
);
const ChecklistsScreen = lazy(() =>
  import("./components/Checklists/ChecklistsScreen").then((m) => ({
    default: m.ChecklistsScreen,
  }))
);
const SettingsScreen = lazy(() =>
  import("./components/Settings/SettingsScreen").then((m) => ({
    default: m.SettingsScreen,
  }))
);

interface SettingsContextValue {
  settings: AppSettings;
  loading: boolean;
  updateChildren: (children: Child[]) => Promise<void>;
  updateLocation: (location: Location | null) => Promise<void>;
  updatePrefs: (prefs: AppPrefs) => Promise<void>;
  updateSelectedCalendars: (ids: string[]) => Promise<void>;
  updateWriteCalendarId: (id: string | null) => Promise<void>;
}

export const SettingsContext = createContext<SettingsContextValue>(
  null as unknown as SettingsContextValue
);

export const useAppSettings = () => useContext(SettingsContext);

function ScreenLoader() {
  return (
    <div className="flex items-center justify-center h-full">
      <div className="w-8 h-8 rounded-full border-2 border-amber-500 border-t-transparent animate-spin" />
    </div>
  );
}

function Layout() {
  useAutoReload();
  const online = useNetworkStatus();

  return (
    <div className="flex flex-col h-full bg-[#fdf8f0]">
      {/* Offline banner */}
      {!online && (
        <div className="flex items-center justify-center gap-2 bg-amber-100 text-amber-800 text-sm py-2 px-4 shrink-0">
          <WifiOff size={16} />
          No internet connection — showing cached data
        </div>
      )}

      {/* Top bar with settings gear */}
      <div className="flex justify-end px-4 py-2 shrink-0">
        <Link
          to="/settings"
          className="p-3 text-stone-400 hover:text-stone-700 active:text-stone-900 transition-colors rounded-xl min-h-[48px] min-w-[48px] flex items-center justify-center"
          aria-label="Settings"
        >
          <Settings size={22} strokeWidth={1.5} />
        </Link>
      </div>

      {/* Main screen content */}
      <main className="flex-1 overflow-hidden">
        <ErrorBoundary>
          <Suspense fallback={<ScreenLoader />}>
            <Routes>
              <Route path="/" element={<DashboardScreen />} />
              <Route path="/calendar" element={<CalendarScreen />} />
              <Route path="/checklists" element={<ChecklistsScreen />} />
              <Route path="/settings" element={<SettingsScreen />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </main>

      <BottomNav />
    </div>
  );
}

export default function App() {
  const settingsHook = useSettings();

  return (
    <SettingsContext.Provider value={settingsHook}>
      <BrowserRouter>
        <Layout />
      </BrowserRouter>
    </SettingsContext.Provider>
  );
}
